import React, { Fragment } from 'react';
import { graphql } from 'gatsby';
import PropTypes from 'prop-types';
import {
  PageHeader,
  SimpleDataTable,
  Section,
  DangerousWrapper,
  ExpandableTextSection,
  toLineItem,
  makeBreadcrumb,
  TimeLine,
  FootNote,
  getFootnotes
} from '@budget-monorepo/shared-ui';

import Layout from '../components/Layout';
import HelmetData from '../components/HelmetData';
import AppropriationFilter from '../components/AppropriationFilter';

const LineItem = (props) => {
  const { data, location } = props;
  const { content, site, vetoLegend } = data;
  const {
    govLevelOne, govLevelTwo, govLevelThree, lineItemNumber, parent: { parent }
  } = content;
  // eslint-disable-next-line camelcase
  const { modifiedTime, mg_mod_date, mg_mod_datetime } = parent;
  const path = location.pathname;
  const pathPrefix = process.env.GATSBY_PATH_PREFIX;
  const relPath = path.replace(pathPrefix, '');
  const { pageTitle, breadcrumbprops } = makeBreadcrumb({
    href: '/enacted',
    text: 'Enacted Budget',
    sections: govLevelOne === govLevelTwo ? [govLevelOne, govLevelThree, lineItemNumber] : [govLevelOne, govLevelTwo, govLevelThree, lineItemNumber]
  });
  const layoutProps = {
    activeIndex: 1,
    pre: () => <AppropriationFilter path={relPath} />,
    above: () => <PageHeader category={toLineItem(content.lineItemNumber)} title={content.lineItemName} />,
    pageTitle: toLineItem(pageTitle),
    breadcrumbprops,
    dataTimeStamp: modifiedTime,
    buildTimeStamp: site.buildTime
  };
  const timeLineData = content.status && content.status.map((state) => {
    const details = state.stage === 1 && content.veto && content.veto.language ? (
      <React.Fragment>
        <span>Governor&apos;s veto explanation: </span>
        <DangerousWrapper rawHtml={content.veto.language} htmlTag="span" />
      </React.Fragment>
    ) : '';
    return{
      text: state.language,
      details,
      completed: state.completed
    };
  });
  // metadata for the page
  const metaTitle = `${content.lineItemNumber}: ${content.lineItemName}`;
  const metaDescription = `View ${content.lineItemName} spending and appropriation data.`;
  return(
    <Fragment>
      <HelmetData {...{
        metaTitle,
        metaDescription,
        mg_mod_date,
        mg_mod_datetime
      }}
      />
      <Layout {...layoutProps}>
        <Section>
          {content.lineItemLanguage && <ExpandableTextSection text={content.lineItemLanguage} minHeight={200} htmlTag="div" vetoLegend={vetoLegend} />}
          {content.veto && content.veto.types && vetoLegend && vetoLegend.legend && content.veto.types.map((type) => {
            const legend = vetoLegend.legend.find((item) => item.outerWrapper === type.wrapper);
            return(
              <span className={legend.outerWrapper}>
                <span className={legend.innerWrapper}>
                  <FootNote index={legend.index}>{type.text}</FootNote>
                </span>
              </span>
            );
          })}
          {content.status && (
            <TimeLine data={timeLineData} align="right" />
          )}
          {content.budgetTracking && content.budgetTracking
            && (
            <React.Fragment>
              <h3>{content.budgetTracking.title}</h3>
              <SimpleDataTable title={`${metaTitle} ${content.budgetTracking.title}`} table={content.budgetTracking} maxColWidth={100} download={location.pathname} />
            </React.Fragment>
            )
          }
          {content.historicalBudget && content.historicalBudget.data
            && (
            <React.Fragment>
              <h3>{content.historicalBudget.title}</h3>
              <SimpleDataTable title={`${metaTitle} ${content.historicalBudget.title}`} table={content.historicalBudget} maxColWidth={100} download={location.pathname} />
            </React.Fragment>
            )
          }
          {content.spendingCategory && content.spendingCategory.data
            && (
            <React.Fragment>
              <h3>{content.spendingCategory.title}</h3>
              <SimpleDataTable title={`${metaTitle} ${content.spendingCategory.title}`} table={content.spendingCategory} maxColWidth={100} download={location.pathname} />
            </React.Fragment>
            )
          }
        </Section>
        {content.footnotes && (
          <Section>
            {getFootnotes(content.footnotes)}
          </Section>
        )}
      </Layout>
    </Fragment>
  );
};

LineItem.propTypes = {
  data: PropTypes.object,
  pageContext: PropTypes.object,
  location: PropTypes.object
};

export default LineItem;

export const query = graphql`
  query($slug: String!) {
    site {
      ...SiteMetadata
    }
    content: lineItem(fields: { slug: { eq: $slug } }) {
      lineItemNumber
      lineItemName
      lineItemLanguage
      govLevelOne
      govLevelTwo
      govLevelThree
      gaa
      veto {
        language
        types {
          text
          wrapper
        }
      }
      status {
        stage
        language
        completed
      }
      budgetTracking {
        title
        headers {
          name
          type
          align
          value
          footnote
        }
        data {
          house1
          houseFinal
          senateFinal
          conference
          gaa
          veto
          override
        }
      }
      historicalBudget {
        title
        headers {
          name
          type
          align
          value
          footnote
        }
        data {
          gaaTwoYearsAgo
          gaaOneYearsAgo
          gaaThisYear
          projected
          gaa
        }
      }
      spendingCategory {
        title
        headers {
          name
          type
          align
          value
          footnote
        }
        total {
          spendingCategory
          expendedThreeYearsAgo
          expendedTwoYearsAgo
          expendedOneYearsAgo
          projected
          gaa
        }
        data {
          spendingCategory
          expendedThreeYearsAgo
          expendedTwoYearsAgo
          expendedOneYearsAgo
          projected
          gaa
        }
      }
      # Get the last modified timestamp for the file powering the line item pages
      ...LineItemDataTimeStamp

      footnotes {
        index
        text
      }
    }
    # Fetch the content for the veto legend
    vetoLegend: relatedInformationJson(id: {eq: "vetolegend"}) {
      ...vetoLegend
    }
  }
`;
